import { useState } from "react";
import { Link } from "react-router-dom";
import { DestinationDetail } from "../../../mocks/destinationDetails";

interface Props {
  destination: DestinationDetail;
  onClose: () => void;
  onBook: () => void;
}

const DestinationDetailModal = ({ destination, onClose, onBook }: Props) => {
  const [activeTab, setActiveTab] = useState<"itinerary" | "attractions" | "cost">("itinerary");

  const tabs = [
    { key: "itinerary" as const, label: "行程安排", icon: "ri-map-2-line" },
    { key: "attractions" as const, label: "景點介紹", icon: "ri-landscape-line" },
    { key: "cost" as const, label: "費用說明", icon: "ri-bill-line" },
  ];

  const difficultyColor: Record<string, string> = {
    輕鬆: "bg-green-100 text-green-700",
    中等: "bg-orange-100 text-orange-700",
    進階: "bg-red-100 text-red-700",
    地獄: "bg-zinc-900 text-orange-400",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[92vh] overflow-hidden flex flex-col">
        {/* Hero Image */}
        <div className="relative h-64 flex-shrink-0">
          <img
            src={destination.image}
            alt={destination.name}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />

          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-10 h-10 bg-black/40 hover:bg-black/60 rounded-full flex items-center justify-center transition-colors cursor-pointer z-10"
          >
            <i className="ri-close-line text-white text-xl" />
          </button>

          <div className="absolute bottom-0 left-0 right-0 p-6">
            <div className="flex items-end justify-between">
              <div>
                <span className="bg-orange-500 text-white text-xs font-semibold px-3 py-1 rounded-full mb-3 inline-block">
                  {destination.subtitle}
                </span>
                <h2 className="text-3xl font-bold text-white">{destination.name}</h2>
              </div>
              <div className="text-right">
                <div className={`font-bold ${destination.price === "需要報價" ? "text-orange-300 text-lg" : "text-orange-400 text-2xl"}`}>
                  {destination.price}
                </div>
                {destination.price !== "需要報價" && (
                  <div className="text-white/70 text-sm">/人起</div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Info Badges */}
        <div className="px-6 py-4 border-b border-stone-100 flex flex-wrap gap-3 flex-shrink-0">
          {destination.price !== "需要報價" && (
            <div className="flex items-center space-x-1.5 bg-stone-100 px-3 py-1.5 rounded-full">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-line text-gray-500 text-sm" />
              </div>
              <span className="text-gray-700 text-sm">{destination.duration}</span>
            </div>
          )}
          <div className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-full ${difficultyColor[destination.difficulty] ?? "bg-stone-100 text-gray-700"}`}>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-bar-chart-line text-sm" />
            </div>
            <span className="text-sm">{destination.difficulty}</span>
          </div>
          <div className="flex items-center space-x-1.5 bg-stone-100 px-3 py-1.5 rounded-full">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-sun-line text-gray-500 text-sm" />
            </div>
            <span className="text-gray-700 text-sm">{destination.bestSeason}</span>
          </div>
          <div className="flex items-center space-x-1.5 bg-stone-100 px-3 py-1.5 rounded-full">
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-map-pin-line text-gray-500 text-sm" />
            </div>
            <span className="text-gray-700 text-sm">{destination.startCity} → {destination.endCity}</span>
          </div>
        </div>

        {/* Description */}
        <div className="px-6 pt-4 pb-3 flex-shrink-0">
          <p className="text-gray-600 text-sm leading-relaxed">{destination.description}</p>
        </div>

        {/* 需要報價 CTA Banner */}
        {destination.price === "需要報價" && (
          <div className="mx-6 mb-4 flex-shrink-0 bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center bg-orange-100 rounded-full flex-shrink-0">
              <i className="ri-customer-service-2-line text-orange-500 text-xl" />
            </div>
            <div className="flex-1">
              <p className="text-orange-800 font-semibold text-sm">此路線需要個人化報價</p>
              <p className="text-orange-600 text-xs mt-0.5 leading-relaxed">
                由於行程難度及規劃複雜，請聯絡我們的專業顧問，為您度身訂造最適合的行程方案及費用報價。
              </p>
            </div>
            <a
              href="https://wa.me/85253482337"
              target="_blank"
              rel="nofollow noreferrer"
              className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5 flex-shrink-0"
            >
              <i className="ri-whatsapp-line text-sm" />
              <span>立即聯絡</span>
            </a>
          </div>
        )}

        {/* Highlights */}
        <div className="px-6 pb-4 flex-shrink-0">
          <div className="flex flex-wrap gap-2">
            {destination.highlights.map((h) => (
              <span
                key={h}
                className="inline-flex items-center space-x-1 bg-orange-50 text-orange-700 text-xs px-2.5 py-1 rounded-full"
              >
                <i className="ri-star-fill text-orange-400 text-xs" />
                <span>{h}</span>
              </span>
            ))}
          </div>
        </div>

        {/* Tabs */}
        <div className="px-6 border-b border-stone-100 flex-shrink-0">
          <div className="flex space-x-1">
            {tabs.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`flex items-center space-x-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === tab.key
                    ? "border-orange-500 text-orange-600"
                    : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
              >
                <i className={`${tab.icon} text-base`} />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content - Scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Itinerary Tab */}
          {activeTab === "itinerary" && (
            <div className="space-y-4">
              {destination.itinerary.map((day, idx) => (
                <div key={day.day} className="flex space-x-4">
                  {/* Timeline */}
                  <div className="flex flex-col items-center flex-shrink-0">
                    <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                      {day.day}
                    </div>
                    {idx < destination.itinerary.length - 1 && (
                      <div className="w-0.5 bg-orange-100 flex-1 mt-1" style={{ minHeight: "24px" }} />
                    )}
                  </div>

                  {/* Content */}
                  <div className="flex-1 pb-4">
                    <h4 className="font-semibold text-gray-900 text-sm mb-2">{day.title}</h4>
                    <ul className="space-y-1 mb-3">
                      {day.activities.map((act, i) => (
                        <li key={i} className="flex items-start space-x-2 text-gray-600 text-sm">
                          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <i className="ri-checkbox-circle-line text-orange-400 text-sm" />
                          </div>
                          <span>{act}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="flex flex-wrap gap-3 text-xs">
                      {day.accommodation !== "——" && (
                        <span className="flex items-center space-x-1 text-gray-500">
                          <i className="ri-hotel-line" />
                          <span>{day.accommodation}</span>
                        </span>
                      )}
                      <span className="flex items-center space-x-1 text-gray-500">
                        <i className="ri-restaurant-line" />
                        <span>{day.meals}</span>
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Attractions Tab */}
          {activeTab === "attractions" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {destination.attractions.map((attr) => (
                <div key={attr.name} className="bg-stone-50 rounded-xl p-4">
                  <div className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${attr.icon} text-orange-500 text-lg`} />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-1">{attr.name}</h4>
                      <p className="text-gray-600 text-sm leading-relaxed">{attr.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Cost Tab */}
          {activeTab === "cost" && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                    <i className="ri-check-line text-green-600" />
                  </div>
                  <h4 className="font-semibold text-gray-900">文字報價含</h4>
                </div>
                <ul className="space-y-3">
                  {[
                    "出入境報關費",
                    "所有政府、公安、海關、軍區等的批文，旅行許可",
                    "1 個臨時駕照和牌照及其相關費用，如需額外駕照 HK$800/個",
                    "交強險和旅遊意外險",
                    "全程中文導遊費用（導遊需要坐客人車裡）",
                    "海關保證金",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start space-x-3 text-gray-600 text-sm">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 bg-green-100 rounded-full">
                        <span className="text-green-600 text-xs font-bold">{i + 1}</span>
                      </div>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <div className="flex items-center space-x-2 mb-4">
                  <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                    <i className="ri-close-line text-red-600" />
                  </div>
                  <h4 className="font-semibold text-gray-900">價格不含</h4>
                </div>
                <ul className="space-y-3">
                  {[
                    "客人食宿",
                    "景點門票",
                    "其他個人消費",
                  ].map((item, i) => (
                    <li key={i} className="flex items-start space-x-3 text-gray-600 text-sm">
                      <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5 bg-red-100 rounded-full">
                        <span className="text-red-500 text-xs font-bold">{i + 1}</span>
                      </div>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer CTA */}
        <div className="px-6 py-4 border-t border-stone-100 flex-shrink-0 bg-white">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <span className={`font-bold ${destination.price === "需要報價" ? "text-orange-500 text-lg" : "text-orange-500 text-2xl"}`}>
                {destination.price}
              </span>
              {destination.price !== "需要報價" && (
                <span className="text-gray-500 text-sm ml-1">/人起 · {destination.duration}</span>
              )}
              {destination.price === "需要報價" && (
                <p className="text-gray-400 text-xs mt-0.5">聯絡顧問獲取專屬報價</p>
              )}
            </div>
            <div className="flex items-center gap-2.5 flex-wrap">
              <Link
                to={`/destination/${destination.id}`}
                onClick={onClose}
                className="text-sm text-gray-500 hover:text-orange-500 transition-colors flex items-center gap-1.5 cursor-pointer whitespace-nowrap border border-gray-200 px-4 py-2.5 rounded-full hover:border-orange-300"
              >
                <i className="ri-external-link-line"></i>
                <span>完整行程頁面</span>
              </Link>
              <a
                href={`https://wa.me/85253482337?text=${encodeURIComponent(`你好，我想查詢「${destination.name}」${destination.duration}行程，請問有哪些可用日期及報價？`)}`}
                target="_blank"
                rel="nofollow noreferrer"
                onClick={onClose}
                className="bg-green-500 hover:bg-green-600 text-white px-5 py-2.5 rounded-full font-medium text-sm transition-all flex items-center gap-2 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-base"></i>
                <span>WhatsApp 詢問此行程</span>
              </a>
              {destination.price === "需要報價" ? (
                <button
                  onClick={onBook}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-full font-medium text-sm transition-all flex items-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-mail-send-line text-base"></i>
                  <span>聯絡我們獲取報價</span>
                </button>
              ) : (
                <button
                  onClick={onBook}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-5 py-2.5 rounded-full font-medium text-sm transition-all flex items-center gap-2 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-calendar-check-line text-base"></i>
                  <span>立即預訂</span>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DestinationDetailModal;
